import type { FactureAchat, FactureAchatLigne } from '@shared/types/FactureAchat';
import type { Ecriture } from '@shared/types/Ecriture';
import { roundFCFA } from '@shared/utils/round';
import { uuid } from '@shared/utils/uuid';
import { compteTvaDeductible } from '@domain/fiscalite/CompteTvaDeductible';

export interface CalculFactureAchatInput {
  fournisseur_id: string;
  lignes: Omit<FactureAchatLigne, 'montant_ht' | 'montant_tva' | 'montant_ttc'>[];
  precompte_taux?: number;
}

/**
 * Service de calcul et de comptabilisation d'une facture d'achat.
 */
export class FactureAchatService {
  /** Calcule les totaux d'une facture à partir des lignes saisies. */
  static calculer(input: CalculFactureAchatInput): {
    lignes: FactureAchatLigne[];
    total_ht: number;
    total_tva: number;
    total_ttc: number;
    precompte_is: number;
    net_a_payer: number;
  } {
    const lignes: FactureAchatLigne[] = input.lignes.map((l) => {
      const montant_ht = roundFCFA(l.quantite * l.prix_unitaire);
      const montant_tva = roundFCFA(montant_ht * l.taux_tva);
      return { ...l, montant_ht, montant_tva, montant_ttc: montant_ht + montant_tva };
    });
    const total_ht = lignes.reduce((s, l) => s + l.montant_ht, 0);
    const total_tva = lignes.reduce((s, l) => s + l.montant_tva, 0);
    const total_ttc = total_ht + total_tva;
    let precompte_is = 0;
    if (input.precompte_taux && input.precompte_taux > 0) {
      precompte_is = roundFCFA(total_ht * input.precompte_taux);
    }
    return {
      lignes,
      total_ht,
      total_tva,
      total_ttc,
      precompte_is,
      net_a_payer: total_ttc - precompte_is,
    };
  }

  /** Génère les écritures comptables OHADA depuis une facture validée. */
  static genererEcritures(facture: FactureAchat, fournisseur_compte: string): Ecriture[] {
    const ecritures: Ecriture[] = [];
    const dateEcr = this.formatDate(facture.date_facture);
    const periode = dateEcr.slice(6, 10) + '-' + dateEcr.slice(3, 5);
    const ref = facture.numero;

    // Débit comptes de charges (lignes) avec ventilation analytique
    for (const ligne of facture.lignes) {
      ecritures.push(
        this.ecr(
          facture.tenant_id,
          dateEcr,
          periode,
          'AC',
          ligne.compte_charge,
          ligne.montant_ht,
          0,
          `Achat ${ref}`,
          ref,
          facture.id,
          ligne.axe_cdc,
        ),
      );
      if (ligne.montant_tva > 0) {
        const compte_tva = compteTvaDeductible({
          immobilisation: ligne.immobilisation,
          compteCharge: ligne.compte_charge,
        });
        ecritures.push(
          this.ecr(
            facture.tenant_id,
            dateEcr,
            periode,
            'AC',
            compte_tva,
            ligne.montant_tva,
            0,
            `TVA déd. ${ref}`,
            ref,
            facture.id,
          ),
        );
      }
    }
    // Crédit fournisseur (net à payer)
    ecritures.push(
      this.ecr(
        facture.tenant_id,
        dateEcr,
        periode,
        'AC',
        fournisseur_compte,
        0,
        facture.net_a_payer,
        `Achat ${ref}`,
        ref,
        facture.id,
      ),
    );
    // Précompte IS → crédit 4427 (4424 = IRVM uniquement)
    if (facture.precompte_is && facture.precompte_is > 0) {
      ecritures.push(
        this.ecr(
          facture.tenant_id,
          dateEcr,
          periode,
          'AC',
          '4427',
          0,
          facture.precompte_is,
          `Précompte IS ${ref}`,
          ref,
          facture.id,
        ),
      );
    }
    return ecritures;
  }

  private static ecr(
    tenant_id: string,
    date: string,
    periode: string,
    journal: string,
    compte: string,
    debit: number,
    credit: number,
    libelle: string,
    ref: string,
    factureId?: string,
    axe?: string,
  ): Ecriture {
    const e: Ecriture = {
      id: uuid('ecr'),
      tenant_id,
      date,
      periode,
      journal: journal as Ecriture['journal'],
      compte,
      libelle_compte: '',
      libelle,
      debit,
      credit,
      source: 'facture' as const,
      numero_piece: ref,
      facture_id: factureId,
      genere_le: new Date().toISOString(),
    };
    if (axe) e.axe_cdc = axe;
    return e;
  }

  private static formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }
}
